import { FC, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Layout, Row, Spin, Space } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as FolderIcon } from "../../assets/icons/svg/folder-gradient.svg";
import { ReactComponent as ThinkerIcon } from "../../assets/icons/svg/thinker-gradient.svg";
import { getDashboard, listProjects } from "../../common/api/ProjectClient";
import { Dashboard, ProjectSummary, ProjectStatus, ProjectType } from "../../types/ProjectTypes";
import { handleError } from "../../common/ErrorHandling";
import { GenericSegmented } from "../../common/components/GenericSegmented";
import { GenericSelect } from "../../common/GenericSelect";
import { useUserState } from "../../common/UserContext";
import { ProjectSummaryCard } from "./components/ProjectSummaryCard";
import classes from "./ProjectListView.module.less";
import { ModalUploadProjectFiles } from "./components/ModalUploadProjectFiles";
import {
    ModalProjectWizard,
    ProjectTypeTitle,
    ProjectTypeIcon,
} from "./components/ModalProjectWizard";
import { useChatbotFlag } from "../demo/chatbotFlag";
import { ModalGuidedDealIntake } from "../dealintake/demo/ModalGuidedDealIntake";

const { Header, Content } = Layout;

enum TabKey {
    Draft = "Draft",
    Active = "Active",
    Completed = "Completed",
    All = "All",
}

const getInitialTab = (): TabKey => {
    const saved = sessionStorage.getItem("projectListTab");
    return saved && Object.values(TabKey).includes(saved as TabKey)
        ? (saved as TabKey)
        : TabKey.All;
};

const OWNER_FILTER_ALL = "all";
const OWNER_FILTER_MINE = "mine";

const getInitialOwnerFilter = (): string => {
    return sessionStorage.getItem("projectListOwnerFilter") ?? OWNER_FILTER_MINE;
};

export const ProjectListView: FC = () => {
    const navigate = useNavigate();
    const { me } = useUserState();
    const [projects, setProjects] = useState<ProjectSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [projectType, setProjectType] = useState(ProjectType.DEAL);
    const [wizardOpen, _setWizardOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<TabKey>(getInitialTab);
    const [ownerFilter, setOwnerFilter] = useState(getInitialOwnerFilter);
    const [dashboard, setDashboard] = useState<Dashboard | undefined>(undefined);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [chatbotEnabled] = useChatbotFlag();
    const [guidedIntakeOpen, setGuidedIntakeOpen] = useState(false);

    const openWizard = (projectType: ProjectType) => {
        if (projectType === ProjectType.DEAL && chatbotEnabled) {
            setGuidedIntakeOpen(true);
            return;
        }
        setProjectType(projectType);
        _setWizardOpen(true);
    };

    const closeWizard = () => {
        _setWizardOpen(false);
    };

    const handleTabChange = (tab: TabKey) => {
        sessionStorage.setItem("projectListTab", tab);
        setActiveTab(tab);
    };

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const allProjects = await listProjects();
            setProjects(allProjects);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboard = async () => {
        try {
            const resp = await getDashboard();
            setDashboard(resp);
        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        fetchProjects();
        fetchDashboard();
    }, []);

    // Filter and sort projects client-side
    const filteredProjects = useMemo(() => {
        let filtered: ProjectSummary[];
        switch (activeTab) {
            case TabKey.Draft:
                filtered = projects.filter((p) => p.status === ProjectStatus.DRAFT);
                break;
            case TabKey.Active:
                filtered = projects.filter((p) => p.status === ProjectStatus.ACTIVE);
                break;
            case TabKey.Completed:
                filtered = projects.filter((p) => p.status === ProjectStatus.COMPLETED);
                break;
            case TabKey.All:
            default:
                filtered = projects;
                break;
        }
        if (ownerFilter === OWNER_FILTER_MINE) {
            filtered = filtered.filter((p) => p.ownerId === me.id || p.creatorId === me.id);
        }
        // Sort by lastUpdateDate descending (most recent first)
        return filtered.sort(
            (a, b) => new Date(b.lastUpdateDate).getTime() - new Date(a.lastUpdateDate).getTime()
        );
    }, [projects, activeTab, ownerFilter, me.id]);

    const documentCount = dashboard?.count.total.documentCount;

    return (
        <Layout className={classes.layout}>
            <Header className={classes.header}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Row>
                        <Col flex={1}>
                            <h2 style={{ margin: 0 }}>
                                Procurement Hub
                                {documentCount && documentCount > 0 ? (
                                    <span className={classes.documentsCount}>
                                        <span className={classes.count}>{documentCount}</span>
                                        <span className={classes.label}> uploaded files</span>
                                    </span>
                                ) : null}
                            </h2>
                            {!chatbotEnabled && (
                                <p style={{ margin: 0 }}>
                                    Upload your contracts and submit for analysis to start
                                    optimizing your vendor spend.
                                </p>
                            )}
                        </Col>
                        <Col>
                            <Link to="/order/new">
                                <Space>
                                    Legacy view
                                    <ArrowRightOutlined />
                                </Space>
                            </Link>
                        </Col>
                    </Row>

                    {chatbotEnabled ? (
                        <Row>
                            <Col span={24}>
                                <Button
                                    type="primary"
                                    onClick={() => setGuidedIntakeOpen(true)}
                                    className={classes.guidedActionButton}
                                >
                                    <ThinkerIcon className={classes.guidedActionIcon} />
                                    <span className={classes.guidedActionText}>
                                        Hello Demo Customer, upload your contracts and submit for
                                        analysis to start optimizing your vendor spend.
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    ) : (
                        <Row gutter={16}>
                            <Col span={8}>
                                <Button
                                    type="primary"
                                    onClick={() => openWizard(ProjectType.DEAL)}
                                    className={classes.actionButton}
                                >
                                    <span style={{ margin: "12px auto" }}>
                                        {ProjectTypeIcon[ProjectType.DEAL]}
                                    </span>
                                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                                        {ProjectTypeTitle[ProjectType.DEAL]}
                                    </span>
                                    <span>Upload single contracts to review and analyze.</span>
                                </Button>
                            </Col>
                            <Col span={8}>
                                <Button
                                    type="primary"
                                    onClick={() => openWizard(ProjectType.RFX)}
                                    className={classes.actionButton}
                                >
                                    <span style={{ margin: "12px auto" }}>
                                        {ProjectTypeIcon[ProjectType.RFX]}
                                    </span>
                                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                                        {ProjectTypeTitle[ProjectType.RFX]}
                                    </span>
                                    <span>Upload proposals to compare and analyze.</span>
                                </Button>
                            </Col>
                            <Col span={8}>
                                <Button
                                    type="primary"
                                    onClick={() => setUploadModalOpen(true)}
                                    className={classes.actionButton}
                                >
                                    <FolderIcon style={{ margin: "12px auto" }} />
                                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                                        Batch contract upload
                                    </span>
                                    <span>
                                        Upload single contract folders or multiple vendor
                                        contracts.
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Space>
            </Header>

            <Content className={classes.content}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Row align="middle" gutter={24}>
                        <Col>
                            <GenericSelect
                                style={{ width: 160 }}
                                value={ownerFilter}
                                onChange={(value) => {
                                    sessionStorage.setItem("projectListOwnerFilter", value);
                                    setOwnerFilter(value);
                                }}
                                options={[
                                    { label: "My projects", value: OWNER_FILTER_MINE },
                                    { label: "All projects", value: OWNER_FILTER_ALL },
                                ]}
                            />
                        </Col>
                        <Col>
                            <GenericSegmented
                                options={[
                                    {
                                        label: (
                                            <span className={classes.segmentedLabel}>
                                                {TabKey.Draft}
                                            </span>
                                        ),
                                        value: TabKey.Draft,
                                    },
                                    {
                                        label: (
                                            <span className={classes.segmentedLabel}>
                                                {TabKey.Active}
                                            </span>
                                        ),
                                        value: TabKey.Active,
                                    },
                                    {
                                        label: (
                                            <span className={classes.segmentedLabel}>
                                                {TabKey.Completed}
                                            </span>
                                        ),
                                        value: TabKey.Completed,
                                    },
                                    {
                                        label: (
                                            <span className={classes.segmentedLabel}>
                                                {TabKey.All}
                                            </span>
                                        ),
                                        value: TabKey.All,
                                    },
                                ]}
                                value={activeTab}
                                onChange={(value) => handleTabChange(value as TabKey)}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col span={24}>
                            {loading ? (
                                <Card>
                                    <div style={{ textAlign: "center", padding: 48 }}>
                                        <Spin size="large" />
                                    </div>
                                </Card>
                            ) : filteredProjects.length === 0 ? (
                                <Card>
                                    <div
                                        style={{
                                            textAlign: "center",
                                            padding: 48,
                                            color: "rgba(0, 0, 0, 0.45)",
                                        }}
                                    >
                                        {activeTab === TabKey.All
                                            ? "No projects yet. Use the buttons above to analyze contracts or batch upload folders."
                                            : `No ${activeTab.toLowerCase()} projects`}
                                    </div>
                                </Card>
                            ) : (
                                filteredProjects.map((project) => (
                                    <ProjectSummaryCard
                                        key={project.id}
                                        project={project}
                                        onUpdate={fetchProjects}
                                    />
                                ))
                            )}
                        </Col>
                    </Row>
                </Space>
            </Content>

            <ModalProjectWizard
                open={wizardOpen}
                defaultProjectType={projectType}
                onClose={() => {
                    closeWizard();
                    handleTabChange(TabKey.Draft);
                    fetchProjects();
                }}
                onSaveAsDraft={(project) => {
                    closeWizard();
                    navigate(`/project/${project.id}`);
                }}
                onComplete={(project, analysisVersion) => {
                    closeWizard();
                    navigate(`/project/${project.id}/analysis/${analysisVersion}`);
                }}
            />

            <ModalUploadProjectFiles
                open={uploadModalOpen}
                defaultProjectType={ProjectType.DEAL}
                onClose={() => setUploadModalOpen(false)}
                onComplete={() => setUploadModalOpen(false)}
                onUploadComplete={fetchProjects}
            />

            <ModalGuidedDealIntake
                open={guidedIntakeOpen}
                onClose={() => setGuidedIntakeOpen(false)}
            />
        </Layout>
    );
};
